/** Centralized activity type configuration */

export interface ActivityTypeConfig {
  label: string
  accent: string                // tailwind color name (e.g. 'cyan', 'red')
  defaultTimeLimit: number | null
  hasQuestionBank: boolean
}

export const ACTIVITY_TYPE_CONFIG: Record<string, ActivityTypeConfig> = {
  mcq:                 { label: 'Multiple Choice',     accent: 'blue',    defaultTimeLimit: 30,  hasQuestionBank: true  },
  coding:              { label: 'Blockly Coding',      accent: 'cyan',    defaultTimeLimit: 300, hasQuestionBank: true  },
  potato_pirates:      { label: 'Potato Pirates',       accent: 'orange',  defaultTimeLimit: 300, hasQuestionBank: false },
  coding_quiz:         { label: 'Coding Quiz',         accent: 'indigo',  defaultTimeLimit: 45,  hasQuestionBank: true  },
  cyber_hygiene_quiz:  { label: 'Cyber Hygiene Quiz',  accent: 'amber',   defaultTimeLimit: 45,  hasQuestionBank: true  },
  phishing_quiz:       { label: 'Phishing Quiz',       accent: 'red',     defaultTimeLimit: 60,  hasQuestionBank: true  },
  packet_analysis:     { label: 'Packet Analysis',     accent: 'violet',  defaultTimeLimit: 90,  hasQuestionBank: true  },
  command_shell:       { label: 'Command Shell',       accent: 'emerald', defaultTimeLimit: 120, hasQuestionBank: true  },
  python_repl:         { label: 'Python Coding',       accent: 'teal',    defaultTimeLimit: 180, hasQuestionBank: true  },
  breach_check:        { label: 'Breach Check',         accent: 'rose',    defaultTimeLimit: 120,  hasQuestionBank: false },
  password_check:      { label: 'Password Check',       accent: 'amber',   defaultTimeLimit: 60,   hasQuestionBank: false },
  google_slides:       { label: 'Google Slides',       accent: 'blue',    defaultTimeLimit: null, hasQuestionBank: false },
  youtube_video:       { label: 'YouTube Video',       accent: 'red',     defaultTimeLimit: null, hasQuestionBank: false },
  pdf_slideshow:       { label: 'PDF Slideshow',       accent: 'orange',  defaultTimeLimit: null, hasQuestionBank: false },
}

export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const
export type Difficulty = (typeof DIFFICULTIES)[number]

export const DIFFICULTY_COLORS: Record<string, string> = {
  easy:   'bg-green-500/15 text-green-500 border-green-500/30',
  medium: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/30',
  hard:   'bg-red-500/15 text-red-500 border-red-500/30',
}

/** Get the display label for an activity type */
export function getActivityTypeLabel(type: string): string {
  return ACTIVITY_TYPE_CONFIG[type]?.label || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

/** Get the accent color for an activity type */
export function getActivityTypeAccent(type: string): string {
  return ACTIVITY_TYPE_CONFIG[type]?.accent || 'gray'
}

/** Tailwind-safe accent class sets keyed by color name */
export interface AccentClasses {
  text: string
  textHover: string
  bgSubtle: string
  borderSubtle: string
  bgSolid: string
}

const ACCENT_CLASS_MAP: Record<string, AccentClasses> = {
  blue:    { text: 'text-blue-500',    textHover: 'hover:text-blue-400',    bgSubtle: 'bg-blue-500/20',    borderSubtle: 'border-blue-500/40',    bgSolid: 'bg-blue-500' },
  cyan:    { text: 'text-cyan-500',    textHover: 'hover:text-cyan-400',    bgSubtle: 'bg-cyan-500/20',    borderSubtle: 'border-cyan-500/40',    bgSolid: 'bg-cyan-500' },
  indigo:  { text: 'text-indigo-500',  textHover: 'hover:text-indigo-400',  bgSubtle: 'bg-indigo-500/20',  borderSubtle: 'border-indigo-500/40',  bgSolid: 'bg-indigo-500' },
  amber:   { text: 'text-amber-500',   textHover: 'hover:text-amber-400',   bgSubtle: 'bg-amber-500/20',   borderSubtle: 'border-amber-500/40',   bgSolid: 'bg-amber-500' },
  red:     { text: 'text-red-500',     textHover: 'hover:text-red-400',     bgSubtle: 'bg-red-500/20',     borderSubtle: 'border-red-500/40',     bgSolid: 'bg-red-500' },
  violet:  { text: 'text-violet-500',  textHover: 'hover:text-violet-400',  bgSubtle: 'bg-violet-500/20',  borderSubtle: 'border-violet-500/40',  bgSolid: 'bg-violet-500' },
  emerald: { text: 'text-emerald-500', textHover: 'hover:text-emerald-400', bgSubtle: 'bg-emerald-500/20', borderSubtle: 'border-emerald-500/40', bgSolid: 'bg-emerald-500' },
  teal:    { text: 'text-teal-500',    textHover: 'hover:text-teal-400',    bgSubtle: 'bg-teal-500/20',    borderSubtle: 'border-teal-500/40',    bgSolid: 'bg-teal-500' },
  orange:  { text: 'text-orange-500',  textHover: 'hover:text-orange-400',  bgSubtle: 'bg-orange-500/20',  borderSubtle: 'border-orange-500/40',  bgSolid: 'bg-orange-500' },
  rose:    { text: 'text-rose-500',    textHover: 'hover:text-rose-400',    bgSubtle: 'bg-rose-500/20',    borderSubtle: 'border-rose-500/40',    bgSolid: 'bg-rose-500' },
  gray:    { text: 'text-gray-500',    textHover: 'hover:text-gray-400',    bgSubtle: 'bg-gray-500/20',    borderSubtle: 'border-gray-500/40',    bgSolid: 'bg-gray-500' },
}

/** Get Tailwind-safe accent classes for an activity type */
export function getAccentClasses(type: string): AccentClasses {
  const accent = ACTIVITY_TYPE_CONFIG[type]?.accent || 'gray'
  return ACCENT_CLASS_MAP[accent] || ACCENT_CLASS_MAP.gray
}

/** Wireshark-style protocol row colors for the packet analysis viewer */
export const PROTOCOL_COLORS: Record<string, string> = {
  TCP:   'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  UDP:   'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  DNS:   'bg-blue-50 dark:bg-blue-800/40 text-blue-600 dark:text-blue-200',
  HTTP:  'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  HTTPS: 'bg-yellow-50 dark:bg-yellow-800/40 text-yellow-600 dark:text-yellow-200',
  TLS:   'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  ARP:   'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  ICMP:  'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300',
  FTP:   'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  SMTP:  'bg-orange-50 dark:bg-orange-800/40 text-orange-600 dark:text-orange-200',
  DHCP:  'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
  SSH:   'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
  TELNET:'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  RST:   'bg-red-50 dark:bg-red-800/40 text-red-600 dark:text-red-200',
  SYN:   'bg-green-50 dark:bg-green-800/40 text-green-600 dark:text-green-200',
}
